import Vue from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import vuetify from './plugins/vuetify'
import '@babel/polyfill'
import 'roboto-fontface/css/roboto/roboto-fontface.css'
import '@mdi/font/css/materialdesignicons.css'
import { sync } from 'vuex-router-sync' //  vue-router store sync used for titles
import interceptor from './helpers/interceptor' // http interceptors
import { Auth0Plugin } from './auth0-plugin'

Vue.config.productionTip = false

// Compute base suffix for runtime settings lookup
const BASE_SUFFIX = (process.env.VUE_APP_SELFSUFFIX || '').replace(/\/$/, '')
const AUTH0_SETTINGS_URL = `${BASE_SUFFIX}/settings/auth0.json`

async function loadAuth0RuntimeConfig() {
  try {
    const res = await fetch(AUTH0_SETTINGS_URL, { cache: 'no-store' })
    if (res.ok) {
      const cfg = await res.json()
      return {
        domain: cfg.domain || process.env.VUE_APP_AUTH0_DOMAIN,
        clientId: cfg.clientId || process.env.VUE_APP_AUTH0_CLIENT_ID,
        audience: cfg.audience || process.env.VUE_APP_AUTH0_AUDIENCE,
        redirectUri:
          cfg.redirectUri ||
          process.env.VUE_APP_AUTH0_CALLBACK_URL ||
          `${window.location.origin}${BASE_SUFFIX}/callback`
      }
    }
  } catch (e) {
    // ignore and fallback to env
  }
  return {
    domain: process.env.VUE_APP_AUTH0_DOMAIN,
    clientId: process.env.VUE_APP_AUTH0_CLIENT_ID,
    audience: process.env.VUE_APP_AUTH0_AUDIENCE,
    redirectUri:
      process.env.VUE_APP_AUTH0_CALLBACK_URL ||
      `${window.location.origin}${BASE_SUFFIX}/callback`
  }
}

async function bootstrap() {
  const runtimeAuth0 = await loadAuth0RuntimeConfig()

  Vue.use(Auth0Plugin, {
    ...runtimeAuth0,
    onRedirectCallback: (appState) => {
      router.push(
        appState && appState.targetPath ? appState.targetPath : '/dashboard'
      )
    }
  })

  // One-time cleanup: remove any legacy tokens persisted in localStorage
  try {
    const legacyKeys = [
      'authenticated',
      'access_token',
      'refresh_token',
      'id_token'
    ]
    legacyKeys.forEach((k) => localStorage.removeItem(k))
  } catch {}

  // Make Auth0 available globally for API calls
  Vue.config.globalProperties = Vue.config.globalProperties || {}
  Vue.config.globalProperties.$auth0 = Vue.prototype.$auth0

  sync(store, router) // adds route module into vuex store
  interceptor() // axios interceptor for the JWT token

  const app = new Vue({
    router,
    store,
    vuetify,
    render: (h) => h(App)
  }).$mount('#app')

  // Make Vue instance globally available for API calls to access Auth0
  window.Vue = Vue
  if (window.Cypress) {
    window.app = app
  }

  // Hydrate organization settings (currency/timezone/plan) from backend
  let settingsLoaded = false
  const hydrateSettings = async () => {
    if (settingsLoaded) return

    try {
      await store.dispatch('settings/fetchSettings')
      settingsLoaded = Boolean(store.state.settings.lastLoadedAt)
    } catch (e) {
      // Settings action handles its own fallback state.
    }
  }

  try {
    if (Vue.prototype.$auth0?.$on) {
      Vue.prototype.$auth0.$on('authStateChanged', hydrateSettings)
    }
    hydrateSettings()
  } catch {
    // ignore
  }
}

bootstrap()
